import { useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, apiDownload } from "../lib/api";
import type {
  CustomersReport,
  LicensesReport,
  Marketer,
  MarketerReport,
  Plan,
  Product,
  ProjectsReport,
  SalesBreakdown,
  SalesReport,
  StatusCount,
  SubscriptionsReport,
} from "../lib/types";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Figure,
  Input,
  PageHeader,
  Select,
  Table,
  TableWrap,
  Td,
  Th,
  formatMoney,
  statusLabel,
} from "../components/ui";

/**
 * An instrument, not a dashboard. Every figure is a number with a label beside
 * it: a chart nobody can read a value off is a worse answer than the value.
 *
 * Money is shown per currency throughout, because the server refuses to add
 * currencies together and this screen must not undo that by stacking two rows
 * and letting the eye do the sum.
 */
export function Reports() {
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [productId, setProductId] = useState("");
  const [planId, setPlanId] = useState("");
  const [marketerId, setMarketerId] = useState("");

  const filters = new URLSearchParams();
  // A date alone means midnight; the end of a range has to mean the end of that
  // day, or picking a single day returns nothing.
  if (since.length > 0) filters.set("since", `${since}T00:00:00.000Z`);
  if (until.length > 0) filters.set("until", `${until}T23:59:59.999Z`);
  if (productId.length > 0) filters.set("productId", productId);
  if (planId.length > 0) filters.set("planId", planId);
  if (marketerId.length > 0) filters.set("marketerId", marketerId);

  // One query string for all six, so the cards on this screen can never turn
  // out to have covered different periods.
  const query = filters.toString();

  const sales = useQuery({
    queryKey: ["report", "sales", query],
    queryFn: () => api<SalesReport>(`/api/v1/admin/reports/sales?${query}`),
  });
  const customers = useQuery({
    queryKey: ["report", "customers", query],
    queryFn: () => api<CustomersReport>(`/api/v1/admin/reports/customers?${query}`),
  });
  const projects = useQuery({
    queryKey: ["report", "projects", query],
    queryFn: () => api<ProjectsReport>(`/api/v1/admin/reports/projects?${query}`),
  });
  const subscriptions = useQuery({
    queryKey: ["report", "subscriptions", query],
    queryFn: () => api<SubscriptionsReport>(`/api/v1/admin/reports/subscriptions?${query}`),
  });
  const licenses = useQuery({
    queryKey: ["report", "licenses", query],
    queryFn: () => api<LicensesReport>(`/api/v1/admin/reports/licenses?${query}`),
  });
  const marketerRows = useQuery({
    queryKey: ["report", "marketers", query],
    queryFn: () => api<MarketerReport[]>(`/api/v1/admin/reports/marketers?${query}`),
  });

  const products = useQuery({
    queryKey: ["products"],
    queryFn: () => api<Product[]>("/api/v1/admin/products"),
  });
  const plans = useQuery({
    queryKey: ["plans", productId],
    queryFn: () =>
      api<Plan[]>(
        productId.length === 0 ? "/api/v1/admin/plans" : `/api/v1/admin/plans?productId=${productId}`,
      ),
  });
  const marketers = useQuery({
    queryKey: ["marketers"],
    queryFn: () => api<Marketer[]>("/api/v1/admin/marketers"),
  });

  const exportCsv = useMutation({
    mutationFn: () =>
      apiDownload(`/api/v1/admin/reports/sales.csv?${query}`, "servicefinder-sales.csv"),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        lede="What has been sold, what is running, and what is about to lapse. Read-only — nothing on this screen changes anything."
        action={
          <Button disabled={exportCsv.isPending} onClick={() => exportCsv.mutate()}>
            {exportCsv.isPending ? "Preparing…" : "Export sales CSV"}
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Field label="From">
          <Input type="date" value={since} onChange={(event) => setSince(event.target.value)} />
        </Field>
        <Field label="To">
          <Input type="date" value={until} onChange={(event) => setUntil(event.target.value)} />
        </Field>
        <Field label="Product">
          <Select
            value={productId}
            onChange={(event) => {
              setProductId(event.target.value);
              // A plan belongs to one product, so a plan chosen under the old
              // product would narrow the whole page to nothing.
              setPlanId("");
            }}
          >
            <option value="">Everything</option>
            {(products.data ?? []).map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Plan">
          <Select value={planId} onChange={(event) => setPlanId(event.target.value)}>
            <option value="">Every plan</option>
            {(plans.data ?? []).map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Marketer">
          <Select value={marketerId} onChange={(event) => setMarketerId(event.target.value)}>
            <option value="">Everyone</option>
            {(marketers.data ?? []).map((marketer) => (
              <option key={marketer.id} value={marketer.id}>
                {marketer.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {exportCsv.isError && <ErrorNote>{(exportCsv.error as Error).message}</ErrorNote>}
      {sales.isError && <ErrorNote>Could not load the reports. Reload to try again.</ErrorNote>}

      <Card
        title="Sales"
        action={<span className="text-xs text-dim">Paid orders, by the day the money arrived</span>}
      >
        {sales.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : sales.data === undefined || sales.data.totals.length === 0 ? (
          <EmptyState>Nothing was paid for in that period.</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Currency</Th>
                  <Th>Orders</Th>
                  <Th>Revenue</Th>
                  <Th>Average order</Th>
                  <Th>Commission earned</Th>
                </tr>
              </thead>
              <tbody>
                {sales.data.totals.map((total) => (
                  <tr key={total.currency} className="border-b border-hairline last:border-0">
                    <Td label="Currency" className="tabular">
                      {total.currency}
                    </Td>
                    <Td label="Orders" className="tabular">
                      {total.orders}
                    </Td>
                    <Td label="Revenue" className="tabular text-ink">
                      {formatMoney(total.revenueCents, total.currency)}
                    </Td>
                    <Td label="Average order" className="tabular text-muted">
                      {formatMoney(total.averageOrderValueCents, total.currency)}
                    </Td>
                    <Td label="Commission earned" className="tabular text-muted">
                      {formatMoney(total.commissionCents, total.currency)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Breakdown title="Revenue by product" rows={sales.data?.byProduct} />
        <Breakdown title="Revenue by plan" rows={sales.data?.byPlan} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Customers">
          <dl className="grid grid-cols-2 gap-px bg-hairline sm:grid-cols-3">
            <Figure label="New in period" value={customers.data?.newInPeriod} />
            <Figure label="Active" hint="Something of theirs is live" value={customers.data?.active} />
            <Figure label="Inactive" value={customers.data?.inactive} />
            <Figure label="On the books" value={customers.data?.total} />
            <Figure
              label="No portal login"
              hint="Normal, not broken"
              value={customers.data?.withoutPortalAccess}
            />
          </dl>
        </Card>

        <Card title="Projects">
          <dl className="grid grid-cols-2 gap-px bg-hairline sm:grid-cols-3">
            {(projects.data?.byStatus ?? []).map((row) => (
              <Figure key={row.status} label={statusLabel(row.status)} value={row.count} />
            ))}
            <Figure
              label="Unpaid"
              hint="No paid order against them"
              value={projects.data?.withoutPaidOrder}
            />
            <Figure
              label="Expiring"
              hint={`Within ${projects.data?.expiringWithinDays ?? 30} days`}
              value={projects.data?.expiring}
            />
          </dl>
        </Card>

        <Card title="Subscriptions">
          <dl className="grid grid-cols-2 gap-px bg-hairline sm:grid-cols-3">
            {(subscriptions.data?.byStatus ?? []).map((row) => (
              <Figure key={row.status} label={statusLabel(row.status)} value={row.count} />
            ))}
            <Figure
              label="Expiring"
              hint={`Within ${subscriptions.data?.expiringWithinDays ?? 30} days`}
              value={subscriptions.data?.expiring}
            />
          </dl>
        </Card>

        <Card title="Licences">
          <dl className="grid grid-cols-2 gap-px bg-hairline sm:grid-cols-3">
            {(licenses.data?.byStatus ?? []).map((row: StatusCount) => (
              <Figure key={row.status} label={statusLabel(row.status)} value={row.count} />
            ))}
            <Figure
              label="Seats in use"
              hint="Activations not revoked"
              value={licenses.data?.activeActivations}
            />
            <Figure label="Activated" hint="In period" value={licenses.data?.activationsInPeriod} />
            <Figure
              label="Deactivated"
              hint="In period"
              value={licenses.data?.deactivationsInPeriod}
            />
            <Figure
              label="Instances seen"
              hint="Checked in during the period"
              value={licenses.data?.instancesSeenInPeriod}
            />
          </dl>
        </Card>
      </div>

      <Card title="Marketers">
        {marketerRows.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : (marketerRows.data?.length ?? 0) === 0 ? (
          <EmptyState>Nothing attributed to a marketer in that period.</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Marketer</Th>
                  <Th>Orders</Th>
                  <Th>Revenue</Th>
                  <Th>Owed</Th>
                  <Th>Paid</Th>
                  <Th>Links</Th>
                  <Th>Converted</Th>
                </tr>
              </thead>
              <tbody>
                {(marketerRows.data ?? []).map((row) => (
                  <tr key={row.marketerId} className="border-b border-hairline last:border-0">
                    <Td label="Marketer">{row.name}</Td>
                    <Td label="Orders" className="tabular">
                      {row.orders}
                    </Td>
                    {/* One line per currency. Stacking them is the point: two
                        rows the eye could add would undo the server's refusal
                        to add them itself. */}
                    <Td label="Revenue" className="tabular">
                      <Stack
                        lines={row.money.map((m) => formatMoney(m.revenueCents, m.currency))}
                      />
                    </Td>
                    <Td label="Owed" className="tabular text-pending">
                      <Stack
                        lines={row.money.map((m) =>
                          formatMoney(
                            m.pendingCommissionCents + m.approvedCommissionCents,
                            m.currency,
                          ),
                        )}
                      />
                    </Td>
                    <Td label="Paid" className="tabular text-muted">
                      <Stack
                        lines={row.money.map((m) => formatMoney(m.paidCommissionCents, m.currency))}
                      />
                    </Td>
                    <Td label="Links" className="tabular text-muted">
                      {row.linksCreated}
                    </Td>
                    <Td label="Converted" className="tabular">
                      {row.conversion === null
                        ? "—"
                        : `${Math.round(row.conversion * 100)}% (${row.linksConverted})`}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      <p className="text-xs text-dim">
        Revenue comes from each order's own snapshot of what was charged, so repricing a plan never
        changes what a past sale earned. Currencies are reported apart and never added together.
      </p>
    </div>
  );
}

function Breakdown({ title, rows }: { title: string; rows: SalesBreakdown[] | undefined }) {
  return (
    <Card title={title}>
      {rows === undefined ? (
        <EmptyState>Loading…</EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState>Nothing sold in that period.</EmptyState>
      ) : (
        <TableWrap>
          <Table>
            <thead className="border-b border-hairline">
              <tr>
                <Th>Name</Th>
                <Th>Orders</Th>
                <Th>Revenue</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.id}-${row.currency}`} className="border-b border-hairline last:border-0">
                  <Td label="Name">{row.name}</Td>
                  <Td label="Orders" className="tabular text-muted">
                    {row.orders}
                  </Td>
                  <Td label="Revenue" className="tabular">
                    {formatMoney(row.revenueCents, row.currency)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Card>
  );
}

/** A figure and what it counts. An em dash until it has loaded, never a zero. */
function Stack({ lines }: { lines: string[] }): ReactNode {
  if (lines.length === 0) return <span className="text-dim">—</span>;
  return (
    <>
      {lines.map((line) => (
        <span key={line} className="block whitespace-nowrap">
          {line}
        </span>
      ))}
    </>
  );
}

